import EventRoleEntry from './roleTokens/EventRoleEntry';
import { tokenText } from './roleTokens/tokenText';
import ElakayImage from '../../components/images/ElakayImage';
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../Layout/Layout";
import { cancelUserRole, getMyRoleChangeRequests, isAuthenticated, readMe, restoreUserRole, verifyPromoToken } from "../auth";
import Accordion from "react-bootstrap/Accordion";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  FaCheckCircle,
  FaRocket,
  FaGift,
  FaShieldAlt
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";

import ElakayAccount from "../Layout/imgs/Elakay-account-1.png";
import RoyaltyUser from "../Layout/imgs/Royalty-user-1.png";
import SellerPub1 from "../Layout/imgs/Seller-pub-1.png";

import { roleUser } from "../pageInside/roleUser";
import { normalizeRoleId, getRoleLevel, shouldShowCancelInstead } from "./roleHierarchy";
import { laguangeElakay } from "../pageInside/dataElakay";
import StepAboutUser from "./userPageExiste/stepAboutUser";
import ShowimagesLoading from "../pageInside/showImagesLoading";
import { PLANS } from "./plans";
import RoleTargetProfiles from "./userStep/RoleTargetProfiles";
import RolePricing from "./userStep/RolePricing";
import InheritedBenefits from "./userStep/InheritedBenefits";
import RoleServiceAccordion from "./userStep/RoleServiceAccordion";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import useElakaySwiper from "../slider/swiperSettings/useElakaySwiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Conditions
import {
  ContactUs,
  TermsConditions,
  ReturnAndRefundPolicy
} from "../pageInside/conditionTherm";

import jsSHA from "jssha";
import "./Userstep.css";
import { elakayAutoText } from "../pageInside/dataElakay";

const sha1 = require("sha1");

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_PAGES_STATE = {
  planAffaire: false,
  termCondition: false,
  userAgree: false
};

const ONE_DAY_MS = 24 * 3600 * 1000;
const THIRTY_DAYS_MS = 30 * ONE_DAY_MS;
const DEFAULT_MAX_AGENT_REQUESTS = 3;

const AgentRequestIdentity = ({ invitation, fallbackName, fallbackRole }) => {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const publicName = invitation.publicName || invitation.initials;

  return (
    <div className="userstep-agent-request-identity">
      <span className="userstep-agent-request-avatar" aria-hidden="true">
        {invitation.avatarUrl && !avatarFailed
          ? <ElakayImage preset="avatar" src={invitation.avatarUrl} alt="" onError={() => setAvatarFailed(true)} />
          : invitation.initials || "EA"}
      </span>
      <span className="userstep-agent-request-copy">
        <strong>{publicName || fallbackName}</strong>
        {/* <small>{invitation.role || fallbackRole}</small> */}
        {invitation.sentAt && <time dateTime={invitation.sentAt}>{new Date(invitation.sentAt).toLocaleDateString()}</time>}
      </span>
    </div>
  );
};

const getAgentRequestUiState = (requests, roleId) => {
  const roleRequests = requests.filter(
    (request) => request.requestedRole?.toLowerCase() === roleId
  );
  const activeRequest = roleRequests.find(
    (request) => request.status === "pending_associate_approval"
  );
  const hasFinalDecision = roleRequests.some(
    (request) =>
      request.status === "approved" ||
      (request.status === "rejected" && request.canReapply !== true)
  );
  const maximumAgentRequests = Number(
    activeRequest?.maximumAgentRequests ??
    roleRequests[0]?.maximumAgentRequests ??
    DEFAULT_MAX_AGENT_REQUESTS
  );
  const submittedRequests = activeRequest
    ? Number(activeRequest.invitationCount ?? activeRequest.invitations?.length ?? 0)
    : 0;
  const remainingRequests = Math.max(0, maximumAgentRequests - submittedRequests);

  return {
    activeRequest,
    latestRequest: roleRequests[0],
    submittedRequests,
    remainingRequests,
    canAddMoreAgentRequests:
      !hasFinalDecision &&
      (!activeRequest || activeRequest.status === "pending_associate_approval") &&
      submittedRequests < maximumAgentRequests,
  };
};

const UserStep = (props) => {
  const { infoSource } = props;
  const { token } = isAuthenticated();
  // Full page hero: Animation Settings -> Swiper Settings ("Full Page Hero").
  const heroSwiper = useElakaySwiper("onboardingHero", { autoNavigation: Boolean(infoSource?.navigation), slideCount: 3 });

  const [loading, setLoading] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [pages, setPages] = useState(DEFAULT_PAGES_STATE);
  const [roleRequests, setRoleRequests] = useState([]);
  const [roleRequestOpen, setRoleRequestOpen] = useState({ Seller: false, Provider: false });
  const [roleRequestAvailability, setRoleRequestAvailability] = useState({
    Seller: { enabled: false, mode: "immediate", unavailableMessage: "comingSoon", startsAt: null, endsAt: null, durationDays: 7, isAvailableNow: false, effectiveStatus: "disabled" },
    Provider: { enabled: false, mode: "immediate", unavailableMessage: "comingSoon", startsAt: null, endsAt: null, durationDays: 7, isAvailableNow: false, effectiveStatus: "disabled" },
  });

  const initialAgreeState = useMemo(() => {
    return PLANS.reduce((acc, plan) => {
      acc[plan.id] = false;
      return acc;
    }, {});
  }, []);

  const [agreeChecked, setAgreeChecked] = useState(initialAgreeState);
  // "View included benefits" toggle (redesign part 8) -- Seller/Provider show a one-line "Includes
  // X Benefits" note by default instead of repeating every inherited group; this expands it inline.
  const [expandedInherited, setExpandedInherited] = useState({});
  // Per-role, per-category service accordion (professional redesign ticket §6/§7): keyed by
  // plan.id -> the one open group.key for that role's card (or undefined/null when all closed).
  // Only one category open at a time, matching the outer role accordion's own convention.
  const [expandedServiceGroup, setExpandedServiceGroup] = useState({});

  // Cancel <current role> modal (spec §6/§7) + the "Restore" action while a
  // cancellation is already pending (spec §8) -- one shared state pair,
  // reused across all three sections since the role being cancelled/
  // restored is always the account's CURRENT role, never the section
  // currently open (see mustCancelInsteadOfBecome above).
  const [cancelRoleModal, setCancelRoleModal] = useState({ show: false, reason: "", submitting: false, error: "" });
  const [restoreRoleState, setRestoreRoleState] = useState({ submitting: false, error: "" });

  // ═══════════════════════════════════════════════════════════════════════
  // SELLER/PROVIDER PROMOTION TOKEN — /user-step?t=TOKEN
  // ═══════════════════════════════════════════════════════════════════════
  // The token already knows its Agent and its type (simple/seller/provider):
  // no Agent search/selection is ever shown for a resolved seller/provider
  // token, see stepAboutUser.jsx's use of promoContext.
  const [searchParams] = useSearchParams();
  const promoT = searchParams.get("t");
  const promoRole = searchParams.get("role");
  const [promoContext, setPromoContext] = useState(
    promoT ? { status: "loading" } : { status: "none" }
  );

  useEffect(() => {
    if (!promoT) {
      setPromoContext({ status: "none" });
      return;
    }
    if (!token) return; // /user-step is wrapped in <UserSignin>; resolve once signed in.

    let mounted = true;
    setPromoContext({ status: "loading" });

    verifyPromoToken(token, promoT, promoRole).then((data) => {
      if (!mounted) return;
      if (!data?.success) {
        setPromoContext({
          status: "error",
          error: data?.code ? tokenText(infoSource)(data.code) : data?.error || elakayAutoText("h_userstep_promo_invalid", "Invalid promotion link."),
        });
        return;
      }
      setPromoContext({
        status: "ready",
        token: promoT,
        promotionType: data.promotionType,
        type: data.type,
        entryPath: data.entryPath,
        roleHierarchy: data.roleHierarchy,
        targetRole: data.targetRole,
        requestApproval: data.requestApproval === true,
        expiryDate: data.expiryDate,
        opportunities: data.opportunities,
        capacity: data.capacity,
        title: data.title,
        agent: data.agent,
      });
    });

    return () => { mounted = false; };
  }, [token, promoT, promoRole]);

  const refreshRoleRequests = async () => {
    if (!token) return;
    try {
      const data = await getMyRoleChangeRequests(token);
      setRoleRequests(data?.requests || []);
      if (data?.roleRequestOpen) setRoleRequestOpen(data.roleRequestOpen);
      if (data?.roleRequestAvailability) setRoleRequestAvailability(data.roleRequestAvailability);
    } catch {
      setRoleRequests([]);
    }
  };

  const [urole, setURole] = useState({
    userID: "",
    customer: "",
    seller: "",
    provider: "",
    role: "",
    endDate: "",
    roleName: "",
    level: "",
    UserRoleName: "",
    UserEndDate: "",
    roleCancellation: null
  });

  const { userID, customer, seller, provider } = urole;

  // ═══════════════════════════════════════════════════════════════════════
  // ROYALTY < SELLER < PROVIDER HIERARCHY (Cancel vs. Become)
  // ═══════════════════════════════════════════════════════════════════════
  // Central logic (normalizeRoleId/getRoleLevel/shouldShowCancelInstead,
  // defined once at module level above): the account's CURRENT role
  // (urole.UserRoleName, the same top-level userActiveRole.roleName the
  // backend's own UpdateUserRole/createRequest checks read) decides, per
  // plan, whether "Become <plan>" is offered (a real upgrade) or
  // "Cancel <current role>" must be shown instead -- never duplicated
  // per-section, computed once here and reused by
  // renderActiveBody/renderCancelRoleModal below.
  // null when the account holds no Royalty/Seller/Provider role at all (a
  // simple user, or a different track entirely -- Associate/Investment/
  // Administrator/Family are untouched by this hierarchy, see spec §18).
  const currentRoleId = normalizeRoleId(urole.UserRoleName);
  const mustCancelInsteadOfBecome = (planId) => shouldShowCancelInstead(urole.UserRoleName, planId);
  const currentRoleCancellation = urole.roleCancellation;
  const hasPendingRoleCancellation = currentRoleCancellation?.status === "pending";

  // ═══════════════════════════════════════════════════════════════════════
  // I18N HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const tr = (section, key, fallback = key) => {
    return infoSource?.laguangeElakay?.(section, infoSource.languageAPI)?.[key] || fallback;
  };

  const getPlanPrivileges = (planId) => {
    return infoSource?.laguangeElakay?.("planPrivileges", infoSource.languageAPI)?.[planId] || {};
  };

  const getPrivilegeText = (planId, privilegeKey) => {
    const privileges = getPlanPrivileges(planId);
    return privileges?.[privilegeKey] || privilegeKey;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // USER / ROLE HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const userHasRole = (planId) => {
    if (planId === "royalty") return !!customer;
    if (planId === "seller") return !!seller;
    if (planId === "provider") return !!provider;
    return false;
  };

  const toggleAgree = (planId) => {
    setAgreeChecked((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        next[key] = key === planId ? !prev[key] : false;
      });
      return next;
    });
  };

  const computeEndDate = (plan, hasRole) => {
    const trialMonths = Number(plan.freeTrialMonths || 0);

    if (hasRole) {
      return Date.now() + THIRTY_DAYS_MS;
    }

    return Date.now() + trialMonths * 30 * ONE_DAY_MS;
  };

  const generateRoleHash = (plan, currentUserID) => {
    const roleConfig = roleUser?.[plan.roleIndex];

    if (!roleConfig || !currentUserID) return "";

    const key =
      sha1(roleConfig.code) +
      sha1(roleConfig.role) +
      sha1(currentUserID);

    const shaObj = new jsSHA("SHA-512", "TEXT", {
      hmacKey: { value: key, format: "TEXT" }
    });

    shaObj.update(`${plan.id}:${plan.roleName}:${currentUserID}:${Date.now()}`);
    return shaObj.getHash("HEX");
  };

  const userKeyRole = (plan) => {
    if (!userID) return;

    const hasRole = userHasRole(plan.id);
    const role = generateRoleHash(plan, userID);
    const endDate = computeEndDate(plan, hasRole);
    const level = getRoleLevel(plan.id);

    setPages({
      planAffaire: true,
      termCondition: false,
      userAgree: false
    });

    setShowRole(true);

    setURole((prev) => ({
      ...prev,
      roleName: plan.roleName,
      role,
      level,
      endDate
    }));
  };

  // A resolved Seller/Provider Promotion Token opens the "All to know
  // about …" modal automatically — the visitor followed a link meant for
  // exactly this role, so they shouldn't have to also click "Become …".
  const autoOpenedPromoRoleRef = useRef(false);

  // Target / View Role Details auto-center-scroll -- reuses the existing (uncontrolled) outer
  // role Accordion's own selection state instead of introducing a second one: one DOM ref per
  // role card (react-bootstrap's Accordion.Item forwards its ref to the real .userstep-accordion-
  // item div), keyed by plan.id.
  const roleRefs = useRef({});

  // Fired from Accordion.Body's own onEntered (react-bootstrap's Collapse transition-complete
  // callback) -- NOT from a click handler or a useEffect on some "selectedRole" state, so this can
  // never fire against stale/still-collapsed DOM (spec: don't scroll before the new content is
  // actually rendered/expanded). It only fires when a card transitions closed -> open, so the
  // promo-token auto-open-on-load case (already-open on first render, no transition) and closing a
  // card never trigger a scroll -- only a real user "open this role" action does.
  const scrollRoleIntoView = (planId) => {
    const node = roleRefs.current[planId];
    if (!node) return;
    const prefersReducedMotion =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    node.scrollIntoView?.({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (autoOpenedPromoRoleRef.current === `${promoT}:${promoRole}`) return;
    if (promoContext?.status !== "ready" || promoContext.type === 'EVENT_ROLE_TOKEN') return;
    if (!userID) return; // wait for readUser() to finish loading urole

    const plan = PLANS.find((candidate) => candidate.id === promoContext.promotionType);
    // Also respects the Royalty/Seller/Provider hierarchy gate -- a promo
    // link for a role at or below the account's real current role must not
    // auto-open the Become workflow either (it would just be rejected by
    // the backend's own hierarchy gate anyway).
    if (!plan || userHasRole(plan.id) || mustCancelInsteadOfBecome(plan.id)) return;

    const requestAvailability = roleRequestAvailability[plan.roleName];
    const roleUiState = getAgentRequestUiState(roleRequests, plan.id);
    const isControlledRequest = plan.roleName === "Seller" || plan.roleName === "Provider";
    const isPlanAvailable = plan.available && (
      !isControlledRequest ||
      requestAvailability?.isAvailableNow === true ||
      Boolean(roleUiState.latestRequest)
    );
    // An existing request already shows its own status alert -- don't pop
    // the modal open over it, and respect the same "requests closed" gate
    // the manual "Become …" button honors.
    const isRoleRequestClosed =
      Object.prototype.hasOwnProperty.call(roleRequestOpen, plan.roleName) &&
      roleRequestOpen[plan.roleName] === false;
    if (!isPlanAvailable || roleUiState.latestRequest || isRoleRequestClosed) return;

    autoOpenedPromoRoleRef.current = `${promoT}:${promoRole}`;
    userKeyRole(plan);
  }, [promoContext, userID, roleRequestAvailability, roleRequests, roleRequestOpen, promoT, promoRole]);

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD USER
  // ═══════════════════════════════════════════════════════════════════════

  // Same shape refreshRoleRequests already uses below -- callable again
  // after Cancel/Restore so the UI updates immediately, without a full page
  // reload (spec §15/§16), reusing this exact fetch instead of duplicating
  // its field mapping in the Cancel/Restore handlers.
  const refreshUserRole = async () => {
    if (!token) return;
    try {
      const data = await readMe(token);
      if (data?.error) return;
      setURole((prev) => ({
        ...prev,
        userID: data?.user?._id || "",
        customer: data?.role?.customer || "",
        seller: data?.role?.seller || "",
        provider: data?.role?.provider || "",
        UserRoleName: data?.role?.roleName || "",
        UserEndDate: data?.role?.endDate || "",
        roleCancellation: data?.role?.roleCancellation || null,
        address:
          data?.user?.address?.find?.((item) => item.addressDefault) ||
          data?.user?.address?.[0] ||
          prev.address ||
          {}
      }));
    } catch (err) {
      console.error("Erreur chargement utilisateur :", err);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (!token) return;

    (async () => {
      setLoading(true);
      await refreshUserRole();
      if (mounted) setLoading(false);
    })();
    getMyRoleChangeRequests(token)
      .then((data) => {
        if (mounted) {
          setRoleRequests(data?.requests || []);
          if (data?.roleRequestOpen) setRoleRequestOpen(data.roleRequestOpen);
          if (data?.roleRequestAvailability) setRoleRequestAvailability(data.roleRequestAvailability);
        }
      })
      .catch(() => {
        if (mounted) setRoleRequests([]);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  // ═══════════════════════════════════════════════════════════════════════
  // CANCEL / RESTORE <CURRENT ROLE>
  // ═══════════════════════════════════════════════════════════════════════

  const openCancelRoleModal = () => setCancelRoleModal({ show: true, reason: "", submitting: false, error: "" });
  const closeCancelRoleModal = () => {
    if (cancelRoleModal.submitting) return; // don't close mid-request
    setCancelRoleModal((current) => ({ ...current, show: false }));
  };

  const submitCancelRole = async () => {
    if (cancelRoleModal.submitting) return; // no double-submit on double click
    setCancelRoleModal((current) => ({ ...current, submitting: true, error: "" }));
    try {
      await cancelUserRole(token, cancelRoleModal.reason);
      setCancelRoleModal({ show: false, reason: "", submitting: false, error: "" });
      await refreshUserRole();
    } catch (requestError) {
      // Keep the modal open on error, per spec -- never auto-close on failure.
      setCancelRoleModal((current) => ({ ...current, submitting: false, error: requestError.message }));
    }
  };

  const submitRestoreRole = async () => {
    if (restoreRoleState.submitting) return;
    setRestoreRoleState({ submitting: true, error: "" });
    try {
      await restoreUserRole(token);
      setRestoreRoleState({ submitting: false, error: "" });
      await refreshUserRole();
    } catch (requestError) {
      setRestoreRoleState({ submitting: false, error: requestError.message });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const renderHeroSlider = () => {
    const images = [ElakayAccount, RoyaltyUser, SellerPub1];

    return (
      <div className="userstep-hero-wrapper">
        <Swiper
          key={heroSwiper.key}
          {...heroSwiper.swiperProps}
          className={`userstep-swiper ${heroSwiper.className}`}
          style={heroSwiper.style}
        >
          {images.map((img, i) => (
            <SwiperSlide key={i}>
              <ShowimagesLoading
                style={{
                  width: "100%",
                  maxHeight: "450px",
                  objectFit: "cover"
                }}
                src={img}
                alt={elakayAutoText("h_97f3a702ed4aa9", "Step {value0}", { value0: i + 1 })}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  };

  const renderPageHeader = () => (
    <div className="userstep-page-header">
      <div className="userstep-header-badge">
        <HiSparkles />
        <span>{tr("title", "yourJourney", "Your Journey")}</span>
      </div>

      <h1 className="userstep-header-title">
        {tr("title", "chooseYourRole", "Choose Your Elakay Role")}
      </h1>

      <p className="userstep-header-subtitle">
        {tr(
          "text",
          "textUserStepHero",
          "Start free for 12 months. Unlock premium benefits, tools, and exclusive features tailored to your ambitions."
        )}
      </p>

      <div className="userstep-free-trial-banner">
        <FaGift className="userstep-free-trial-icon" />
        <div className="userstep-free-trial-text">
          <strong>{tr("text", "textFreeTrialBanner1", "12 months free")}</strong>
          <span>
            {tr(
              "text",
              "textFreeTrialBanner2",
              "on your first role — no commitment, cancel anytime"
            )}
          </span>
        </div>
      </div>
    </div>
  );

  // Existing translation first (planPrivileges.<planId>.<key>, unchanged for every "pN" key that
  // was already on the page), falling back to elakayAutoText for genuinely new chip content --
  // same try-existing-then-auto-translate pattern already used elsewhere in this codebase.
  const getChipText = (planId, item) => {
    const existing = getPrivilegeText(planId, item.key);
    return existing !== item.key
      ? existing
      : elakayAutoText(`h_userstep_${planId}_${item.key}`, item.fallback || item.key);
  };

  const getGroupLabel = (group) =>
    elakayAutoText(`h_userstep_group_${group.key}`, group.labelFallback);

  const totalServiceCount = (plan) =>
    (plan.privilegeGroups || []).reduce((sum, group) => sum + (group.items?.length || 0), 0);

  // Redesign: professional /your-step (ticket parts 1-39). Per-category status badges, chips,
  // the service accordion, and the inherited-benefits panel are now owned by dedicated components
  // under ./userStep/ (RoleStatusBadge, RoleServiceAccordion, InheritedBenefits, RolePricing,
  // RoleTargetProfiles) -- this file only wires them to the page's own i18n helpers/state.
  const exploreServices = (plan) => {
    const firstGroupKey = plan.privilegeGroups?.[0]?.key;
    if (firstGroupKey) {
      setExpandedServiceGroup((current) => ({ ...current, [plan.id]: firstGroupKey }));
    }
    document.getElementById(`userstep-services-${plan.id}`)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const renderPlanCard = (plan) => {
    const Icon = plan.icon;
    const hasRole = userHasRole(plan.id);
    const showTrial = !hasRole && Number(plan.freeTrialMonths || 0) > 0;

    return (
      <div
        className={`userstep-plan-card${hasRole ? " userstep-plan-card--current" : ""}`}
        style={{ "--plan-accent": plan.accentColor }}
      >
        {showTrial && (
          <div className="userstep-plan-ribbon">
            <FaGift />
            <span>
              {plan.freeTrialMonths} {tr("label", "monthsFree", "months FREE")}
            </span>
          </div>
        )}

        {hasRole && (
          <div className="userstep-plan-current-badge">
            <FaCheckCircle aria-hidden="true" />
            {elakayAutoText("h_userstep_current_plan", "Current Plan")}
          </div>
        )}

        <div
          className="userstep-plan-icon"
          style={{
            background: `linear-gradient(135deg, ${plan.accentColor} 0%, ${plan.accentColor} 100%)`
          }}
        >
          <Icon />
        </div>

        <h3 className="userstep-plan-title">
          {tr("title", plan.titleKey, plan.roleName)}
        </h3>

        {plan.descriptionFallback && (
          <p className="userstep-plan-description">
            {elakayAutoText(`h_userstep_description_${plan.id}`, plan.descriptionFallback)}
          </p>
        )}

        <RoleTargetProfiles plan={plan} />

        {showTrial && (
          <div className="userstep-plan-free-label">
            <HiSparkles />
            {tr("title", "startFreeUpgradeAnytime", "Start free, upgrade anytime")}
          </div>
        )}

        <RolePricing plan={plan} showTrial={showTrial} tr={tr} />
      </div>
    );
  };

  const renderServicesPanel = (plan) => {
    const containerId = `userstep-services-${plan.id}`;
    return (
      <div className="userstep-plan-services">
        <InheritedBenefits
          plan={plan}
          isExpanded={expandedInherited[plan.id] === true}
          onToggle={() => setExpandedInherited((current) => ({ ...current, [plan.id]: !current[plan.id] }))}
          tr={tr}
          getGroupLabel={getGroupLabel}
          getChipText={getChipText}
        />

        <div className="userstep-plan-features-header">
          <span>{tr("title", "whatsIncluded", "What's included")}</span>
          <span className="userstep-plan-service-count">
            {elakayAutoText("h_userstep_services_count", "{value0}+ services", { value0: totalServiceCount(plan) })}
          </span>
        </div>

        <button type="button" className="userstep-explore-services" onClick={() => exploreServices(plan)}>
          {elakayAutoText("h_userstep_explore_services", "Explore Services")} ↓
        </button>

        <RoleServiceAccordion
          plan={plan}
          groups={plan.privilegeGroups}
          allSettings={infoSource?.allSettings}
          getGroupLabel={getGroupLabel}
          getChipText={(item) => getChipText(plan.id, item)}
          expandedGroupKey={expandedServiceGroup[plan.id]}
          onToggleGroup={(groupKey) =>
            setExpandedServiceGroup((current) => ({
              ...current,
              [plan.id]: current[plan.id] === groupKey ? null : groupKey,
            }))
          }
          containerId={containerId}
        />
      </div>
    );
  };

  const renderActiveBody = (plan) => {
    const hasRole = userHasRole(plan.id);
    const showTrial = !hasRole && Number(plan.freeTrialMonths || 0) > 0;
    // Royalty < Seller < Provider hierarchy: this section's target role is
    // at or below the account's real CURRENT role (currentRoleId,
    // computed once above from urole.UserRoleName) -- the
    // checkbox/CTA that would (re)request it are hidden and a
    // "Cancel <current role>" action takes their place instead (see the
    // /your-step spec). Generalizes the old Royalty-only
    // "isRoyaltyAlreadyHeld" gate to all three plans -- Royalty-already-held
    // is just the currentRoleId === "royalty" case of this same formula.
    const mustCancelInstead = mustCancelInsteadOfBecome(plan.id);
    // Royalty is always immediate/self-service (see UpdateUserRole
    // server-side) -- it has no Agent-approval workflow at all, so it must
    // never read/render the Seller/Provider "request" state (pending/
    // approved/rejected alert, "Add another Agent", the agent-count-based
    // disabled gate). Any pre-existing RoleChangeRequest row for Royalty
    // (e.g. a leftover from before that workflow was removed) is simply
    // ignored here. Same reasoning extends to mustCancelInstead: once the
    // account is already at or above this role, the old
    // pending/approved/rejected Agent-request alert for it is no longer the
    // relevant thing to show -- the Cancel section below takes over.
    const isControlledRequest = !mustCancelInstead && (plan.roleName === "Seller" || plan.roleName === "Provider");
    const {
      latestRequest,
      submittedRequests,
      remainingRequests,
      canAddMoreAgentRequests,
    } = isControlledRequest
      ? getAgentRequestUiState(roleRequests, plan.id)
      : { latestRequest: null, submittedRequests: 0, remainingRequests: 0, canAddMoreAgentRequests: true };
    const isRoleRequestClosed =
      Object.prototype.hasOwnProperty.call(roleRequestOpen, plan.roleName) &&
      roleRequestOpen[plan.roleName] === false;
    // A resolved Seller/Provider Promotion Token only activates that one
    // role's "Become …" CTA (spec: "uniquement Become Seller/Provider").
    const promoActive =
      promoContext?.status === "ready" &&
      (promoContext.promotionType === "seller" || promoContext.promotionType === "provider");
    const isBlockedByPromo = promoActive && promoContext.promotionType !== plan.id;

    return (
      <div className="userstep-accordion-body-content-wrapper">
        {latestRequest && (
          <div className={`alert ${
            latestRequest.status === "approved"
              ? "alert-success"
              : latestRequest.status === "rejected"
                ? "alert-danger"
                : "alert-info"
          }`}>
            {latestRequest.status === "pending_associate_approval" ? (
              <>
                <h3 className="userstep-request-title">
                  {tr("roleChangeRequest", "requestWaiting", "Request Is Waiting")}
                </h3>
                <p>{tr("roleChangeRequest", "requestsSent", "Your requests have been sent to the selected Agents.")}</p>
              </>
            ) : latestRequest.status === "approved"
              ? tr("roleChangeRequest", "requestApproved", "Your role request was approved.")
              : tr("roleChangeRequest", "requestRejected", "Your role request was rejected.")}
            {latestRequest.rejectionReason ? ` ${latestRequest.rejectionReason}` : ""}
            {/* A pending or refused role request never limits the regular account. */}
            {latestRequest.status !== "approved" && (
              <p className="userstep-request-continue mb-0">{tr("roleChangeRequest", "continueRegularAccount", "You can continue using your regular account.")}</p>
            )}
            {latestRequest.invitations?.length > 0 && (
              <ul className="userstep-invitation-statuses">
                {latestRequest.invitations.map((invitation, index) => (
                  <li key={`${invitation.publicName || invitation.initials || "agent"}-${index}`}>
                    <AgentRequestIdentity
                      invitation={invitation}
                      fallbackName={tr("roleChangeRequest", "elakayAgent", "Elakay Agent")}
                      fallbackRole={tr("roleChangeRequest", "agent", "Agent")}
                    />
                    <span>{tr("roleChangeRequest", invitation.status, invitation.status)}</span>
                  </li>
                ))}
              </ul>
            )}
            {latestRequest.status === "pending_associate_approval" && submittedRequests > 0 && remainingRequests > 0 && (
              <p className="userstep-request-limit">
                {tr(
                  "roleChangeRequest",
                  remainingRequests === 1 ? "optionalOneMoreRequest" : "optionalMoreRequests",
                  remainingRequests === 1
                    ? "You can add 1 more Agent request if you’d like."
                    : "You can add up to {count} more Agent requests if you’d like."
                ).replace("{count}", remainingRequests)}
              </p>
            )}
          </div>
        )}

        {canAddMoreAgentRequests && latestRequest && !isRoleRequestClosed && (
          <div className="userstep-add-agent-action">
            <Button
              type="button"
              className="userstep-add-agent-button"
              onClick={() => userKeyRole(plan)}
            >
              {tr("roleChangeRequest", "addAnotherAgent", "Add another Agent")}
            </Button>
          </div>
        )}

        <div className="userstep-accordion-body-content">
          <div className="userstep-role-left">
            {renderPlanCard(plan)}
          </div>

          <div className="userstep-role-right">
            {renderServicesPanel(plan)}
          </div>
        </div>

        <div className="userstep-terms-block">
          {showTrial && (
            <div className="userstep-trial-highlight">
              <FaGift className="userstep-trial-highlight-icon" />
              <div>
                <strong>
                  {plan.freeTrialMonths} {tr("label", "monthsFree", "months FREE")}
                </strong>
                <p>
                  {tr(
                    "text",
                    "textTrialNote",
                    "You won't be charged until your trial ends. Upgrade to the paid plan anytime to keep your benefits."
                  )}
                </p>
              </div>
            </div>
          )}

          <p className="userstep-terms-note">
            {tr("title", "note", "note...")}
          </p>

          <ul className="userstep-terms-list">
            <li>
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowConditions(true);
                }}
                className="userstep-link"
              >
                {tr("helpContact", "termsCondition", "Terms and Conditions")}
              </Link>
            </li>

            <li>
              <Link to="/help-contact" className="userstep-link">
                {tr("helpContact", "HelpContact", "Help & contact")}
              </Link>
            </li>

          {!isRoleRequestClosed && !mustCancelInstead && (
            <li className="userstep-agree-row">
              <input
                type="checkbox"
                id={`agree-${plan.id}`}
                className="userstep-checkbox-input"
                checked={!!agreeChecked[plan.id]}
                onChange={() => toggleAgree(plan.id)}
              />

              <label
                htmlFor={`agree-${plan.id}`}
                className="userstep-switch"
                aria-label={tr("title", "chButAgree", "check the button if your are agree")}
              >
                <span className="userstep-switch-track">
                  <span className="userstep-switch-thumb" />
                </span>
              </label>

              <label
                htmlFor={`agree-${plan.id}`}
                className="userstep-checkbox-label"
              >
                {tr("title", "chButAgree", "check the button if your are agree")}
              </label>
            </li>
          )}
          </ul>

          {/* Start Free / Become action: placed after the agreement switch it depends on. */}
          <div className="userstep-role-cta userstep-role-cta--below-agree">
            {isRoleRequestClosed && !latestRequest && !mustCancelInstead && (
              <p className="userstep-role-request-closed">
                {tr(
                  "roleChangeRequest",
                  "roleRequestClosed",
                  `${plan.roleName} requests are currently closed.`
                )}
              </p>
            )}

            {isBlockedByPromo && (
              <p className="userstep-role-request-closed">
                {tr(
                  "roleChangeRequest",
                  "promoTokenOtherRole",
                  `This promotion link only lets you apply as ${promoContext.promotionType === "seller" ? "Seller" : "Provider"}.`
                )}
              </p>
            )}

            {!latestRequest && !isRoleRequestClosed && !isBlockedByPromo && !mustCancelInstead && <div className="userstep-cta-wrapper">
              <Button
                className={`userstep-btn-primary ${
                  !agreeChecked[plan.id] || !canAddMoreAgentRequests ? "userstep-btn-disabled" : ""
                }`}
                disabled={!agreeChecked[plan.id] || !canAddMoreAgentRequests}
                onClick={() => userKeyRole(plan)}
                style={{
                  background: `linear-gradient(135deg, ${plan.accentColor} 0%, ${plan.accentColor} 100%)`
                }}
              >
                <FaRocket style={{ marginRight: "8px" }} />
                {showTrial
                  ? `${tr("title", "startFreeBecome", "Start Free — Become")} ${plan.roleName}`
                  : `${tr("title", "becomeRole", "Become")} ${plan.roleName}`}
              </Button>
            </div>}

            {mustCancelInstead && renderCancelOrPendingSection()}

            <p className="userstep-cta-helper">
              <FaShieldAlt />
              {showTrial
                ? tr(
                    "text",
                    "textNoCardTrial",
                    `No credit card required. Free for ${plan.freeTrialMonths} months.`
                  )
                : tr(
                    "text",
                    "textSecureCheckout",
                    "Secure checkout. Cancel anytime."
                  )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Rendered by renderActiveBody in place of the Become checkbox/CTA once
  // mustCancelInsteadOfBecome(plan.id) is true -- always about the
  // account's real CURRENT role (currentRoleId/urole.UserRoleName), never
  // the plan/section currently open, so it looks identical no matter which
  // of the three sections triggered it (spec §5).
  const renderCancelOrPendingSection = () => {
    const currentRoleDisplayName =
      urole.UserRoleName || (currentRoleId ? currentRoleId.charAt(0).toUpperCase() + currentRoleId.slice(1) : "");

    if (hasPendingRoleCancellation) {
      const effectiveAtLabel = currentRoleCancellation?.effectiveAt
        ? new Date(currentRoleCancellation.effectiveAt).toLocaleDateString()
        : "";
      return (
        <div className="userstep-cancel-pending alert alert-warning">
          <p>
            {tr(
              "roleChangeRequest",
              "roleCancellationPending",
              "{role} role cancellation pending"
            ).replace("{role}", currentRoleDisplayName)}
          </p>
          {effectiveAtLabel && (
            <p className="userstep-cancel-pending-date">
              {tr("roleChangeRequest", "roleCancellationScheduledFor", "Scheduled for: {date}").replace("{date}", effectiveAtLabel)}
            </p>
          )}
          {restoreRoleState.error && <div className="alert alert-danger">{restoreRoleState.error}</div>}
          <Button
            type="button"
            className="userstep-btn-primary"
            disabled={restoreRoleState.submitting}
            onClick={submitRestoreRole}
          >
            {restoreRoleState.submitting
              ? tr("roleChangeRequest", "restoringRole", "Restoring…")
              : tr("roleChangeRequest", "keepRole", "Keep {role}").replace("{role}", currentRoleDisplayName)}
          </Button>
        </div>
      );
    }

    return (
      <div className="userstep-cta-wrapper userstep-cancel-role-wrapper">
        <Button
          type="button"
          className="userstep-btn-cancel-role"
          onClick={openCancelRoleModal}
        >
          {tr("roleChangeRequest", "cancelRole", "Cancel {role}").replace("{role}", currentRoleDisplayName)}
        </Button>
      </div>
    );
  };

  const renderComingSoonBody = (plan, availability = {}) => {
    const isComingBack = availability.unavailableMessage === "comingBack";
    const unavailableLabel = isComingBack
      ? tr("roleChangeRequest", "comingBack", "COMING BACK")
      : tr("title", "comingSoon", "COMING SOON...");
    return (
    <div className="userstep-accordion-body-content">
      <div className="userstep-role-left">
        {renderPlanCard(plan)}

        <div className={`userstep-coming-soon-block${isComingBack ? " userstep-coming-soon-block--suspended" : ""}`}>
          <div className="userstep-coming-soon-badge">
            <FaShieldAlt />
            <span>{unavailableLabel}</span>
          </div>

          <h3 className="userstep-coming-soon-title">
            {isComingBack
              ? tr("roleChangeRequest", "suspending", "SUSPENDING")
              : tr("title", "selAssList", "Select an Associate from the List on Elakay")}
          </h3>

          <p className="userstep-coming-soon-text">
            {isComingBack
              ? tr("roleChangeRequest", "comingBackSoon", "Coming back soon")
              : availability.effectiveStatus === "expired"
              ? tr("roleChangeRequest", "requestsClosed", "Requests Closed")
              : availability.effectiveStatus === "scheduled" && availability.startsAt
                ? `${tr("title", "comingSoon", "Coming Soon")} — ${tr("roleChangeRequest", "availableOn", "Available on")} ${new Date(availability.startsAt).toLocaleString()}`
                : tr("text", "textComingSoonPlan", "Be the first to know when this plan is available.")}
          </p>
        </div>
      </div>

      <div className="userstep-role-right">
        {renderServicesPanel(plan)}
      </div>
    </div>
    );
  };

  const renderAccordion = () => {
    // A resolved Seller/Provider token opens straight on that plan's panel
    // instead of the default Royalty panel.
    const promoLockedEventKey =
      promoContext?.status === "ready"
        ? PLANS.find((plan) => plan.id === promoContext.promotionType)?.eventKey
        : undefined;

    return (
    <div className="userstep-accordion-container userstep-section">
      {/* Redesign part 3/4: nothing open by default -- three compact cards side by side, one
          section opens on click (a resolved promo/invite token still opens straight on its own
          plan, exactly as before). */}
      <Accordion {...(promoLockedEventKey ? { activeKey: promoLockedEventKey } : {})}>
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const requestAvailability = roleRequestAvailability[plan.roleName];
          const roleUiState = getAgentRequestUiState(roleRequests, plan.id);
          const isControlledRequest = plan.roleName === "Seller" || plan.roleName === "Provider";
          const isPlanAvailable = plan.available && (
            !isControlledRequest ||
            requestAvailability?.isAvailableNow === true ||
            Boolean(roleUiState.latestRequest)
          );
          const showFreePill =
            isPlanAvailable &&
            !userHasRole(plan.id) &&
            Number(plan.freeTrialMonths || 0) > 0;

          return (
            <Accordion.Item
              key={plan.id}
              eventKey={plan.eventKey}
              className="userstep-accordion-item"
              ref={(element) => {
                roleRefs.current[plan.id] = element;
              }}
            >
              <Accordion.Header className="userstep-accordion-header">
                <div className="userstep-accordion-header-content">
                  <span
                    className="userstep-accordion-header-icon"
                    style={{
                      background: `linear-gradient(135deg, ${plan.accentColor} 0%, ${plan.accentColor} 100%)`
                    }}
                  >
                    <Icon />
                  </span>

                  <span className="userstep-accordion-header-titlewrap">
                    <span className="userstep-accordion-header-text">
                      {tr("title", plan.titleKey, `Become ${plan.roleName}`)}
                    </span>
                    <span className="userstep-accordion-header-tagline">
                      {elakayAutoText(`h_userstep_tagline_${plan.id}`, plan.taglineFallback)}
                    </span>
                  </span>

                  {showFreePill && (
                    <span className="userstep-accordion-header-pill userstep-pill-free">
                      <FaGift /> {plan.freeTrialMonths}{" "}
                      {tr("label", "monthsFree", "months FREE")}
                    </span>
                  )}

                  {!isPlanAvailable && (
                    <span className="userstep-accordion-header-pill userstep-pill-soon">
                      {requestAvailability?.unavailableMessage === "comingBack"
                        ? tr("roleChangeRequest", "comingBack", "COMING BACK")
                        : tr("title", "comingSoon", "COMING SOON...")}
                    </span>
                  )}

                  <span className="userstep-accordion-header-footer">
                    <span className="userstep-accordion-header-count">
                      {elakayAutoText("h_userstep_services_count", "{value0}+ services", { value0: totalServiceCount(plan) })}
                    </span>
                    <span className="userstep-accordion-header-viewdetails">
                      {elakayAutoText("h_userstep_view_details", "View Details")} ›
                    </span>
                  </span>
                </div>
              </Accordion.Header>

              <Accordion.Body
                className="userstep-accordion-body"
                onEntered={() => scrollRoleIntoView(plan.id)}
              >
                {isPlanAvailable
                  ? renderActiveBody(plan)
                  : renderComingSoonBody(plan, requestAvailability)}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
    );
  };

  const renderTermsModal = () => (
    <Modal
      size="xl"
      show={showConditions}
      onHide={() => setShowConditions(false)}
      dialogClassName="modal-90w"
      centered
      className="userstep-modal"
      aria-labelledby="userstep-terms-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="userstep-terms-title">
          <h2>{tr("helpContact", "termsCondition", "Terms and Conditions")}</h2>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {TermsConditions()}
        {ReturnAndRefundPolicy()}
        {ContactUs()}
      </Modal.Body>
    </Modal>
  );

  // "Cancel <current role>" confirmation modal (spec §6): always about the
  // account's real CURRENT role, reused across all three sections the same
  // way renderCancelOrPendingSection is (see openCancelRoleModal/
  // submitCancelRole above).
  const renderCancelRoleModal = () => {
    const currentRoleDisplayName =
      urole.UserRoleName || (currentRoleId ? currentRoleId.charAt(0).toUpperCase() + currentRoleId.slice(1) : "");
    const withRole = (template) => template.split("{role}").join(currentRoleDisplayName);

    return (
      <Modal show={cancelRoleModal.show} onHide={closeCancelRoleModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {withRole(tr("roleChangeRequest", "cancelRoleTitle", "Cancel {role} Role"))}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {withRole(tr(
              "roleChangeRequest",
              "cancelRoleBody",
              "Your {role} role will enter a 7-day cancellation period. During this period, you can cancel this request and keep your {role} role. If no action is taken before the end of the 7-day period, the role will be removed automatically."
            ))}
          </p>
          <label htmlFor="cancel-role-reason" className="userstep-checkbox-label">
            {tr("roleChangeRequest", "cancelRoleReasonLabel", "Why do you want to cancel this role? (optional)")}
          </label>
          <textarea
            id="cancel-role-reason"
            className="form-control"
            rows={3}
            maxLength={1000}
            value={cancelRoleModal.reason}
            onChange={(e) => setCancelRoleModal((current) => ({ ...current, reason: e.target.value }))}
            disabled={cancelRoleModal.submitting}
          />
          {cancelRoleModal.error && (
            <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{cancelRoleModal.error}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={cancelRoleModal.submitting}
            onClick={closeCancelRoleModal}
          >
            {tr("Button", "cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={cancelRoleModal.submitting}
            onClick={submitCancelRole}
          >
            {cancelRoleModal.submitting
              ? tr("roleChangeRequest", "submitting", "Submitting…")
              : withRole(tr("roleChangeRequest", "confirmCancelRole", "Confirm Cancel {role}"))}
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  const modalRequestState = getAgentRequestUiState(
    roleRequests,
    urole.roleName?.toLowerCase()
  );
  const modalAgentSlots = modalRequestState.activeRequest
    ? modalRequestState.remainingRequests
    : undefined;

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <Layout
      infoSource={infoSource}
      title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).userStep.t001}
      description={elakayAutoText("h_ab17107b238471", "Select your Elakay plan and unlock benefits, business tools, and provider features.")}
      className="container-fluid"
      SliderPunShow={false}
      loading={loading}
      {...infoSource?.allSettings?.layout}
    >
      <div style={infoSource.styleNav} className="userstep-page-wrapper">
        <div className="userstep-container">
          {promoContext?.type !== 'EVENT_ROLE_TOKEN' && renderHeroSlider()}
          {promoContext?.type !== 'EVENT_ROLE_TOKEN' && renderPageHeader()}
          {promoContext?.status === "loading" && (
            <div className="alert alert-info userstep-section">
              {tr("roleChangeRequest", "promoTokenValidating", "Validating your promotion link…")}
            </div>
          )}
          {promoContext?.status === "error" && (
            <div className="alert alert-danger userstep-section">
              {promoContext.error || tr("roleChangeRequest", "promoTokenInvalid", "This promotion link is invalid or has expired.")}
            </div>
          )}
          {promoContext?.status === "ready" && promoContext.type !== 'EVENT_ROLE_TOKEN' && (
            <div className="alert alert-success userstep-section">
              {tr(
                "roleChangeRequest",
                "promoTokenReady",
                `You're applying through Agent ${promoContext.agent?.displayName || ""}.`
              )}
            </div>
          )}
          {promoContext?.status === 'ready' && promoContext.type === 'EVENT_ROLE_TOKEN'
            ? <EventRoleEntry key={promoT} infoSource={infoSource} context={promoContext} token={token} />
            : (!promoT || promoContext?.status === 'ready') && renderAccordion()}
        </div>

        {renderTermsModal()}
        {renderCancelRoleModal()}

        <StepAboutUser
          showRole={showRole}
          setShowRole={setShowRole}
          laguangeElakay={laguangeElakay}
          infoSource={infoSource}
          token={token}
          userActiveRole={urole}
          onRequestCreated={refreshRoleRequests}
          maxAgentSelections={modalAgentSlots}
          pages={pages}
          setPages={setPages}
          promoContext={promoContext?.status === "ready" ? promoContext : null}
        />
      </div>
    </Layout>
  );
};

export default UserStep;
